import type { ReactNode } from "react";

import { cn } from "~/lib/ui";

type CartRowSlots = {
  imgSm?: ReactNode;
  imgLg?: ReactNode;
  name?: ReactNode;
  variants?: ReactNode;
  actions?: ReactNode;
  cost?: ReactNode;
  qty?: ReactNode;
  total?: ReactNode;
  rm?: ReactNode;
  bundles?: ReactNode;
  messaging?: ReactNode;
};

export const CartRowLayout = ({
  isStale = false,
  isSavingForLater = false,
  isMovingToCart = false,
  imgSm,
  imgLg,
  name,
  variants,
  actions,
  cost,
  qty,
  total,
  rm,
  bundles,
  messaging,
}: CartRowSlots & {
  isStale?: boolean;
  isSavingForLater?: boolean;
  isMovingToCart?: boolean;
}) => (
  <li
    className={cn(
      "grid gap-x-4 gap-y-2 py-4 md:gap-x-2 md:py-5",
      "[grid-template-areas:'img_costAndRm''img_name''img_varsActs''msg_msg''bundles_bundles''._qty''._total']",
      "sm:[grid-template-areas:'name_name_name_name_name_name''img_varsActs_cost_qty_total_rm''img_msg_msg_msg_msg_msg''img_bundles_bundles_bundles_bundles_bundles']",
      "grid-cols-[auto_1fr]",
      "sm:grid-cols-[auto_4fr_3fr_3fr_3fr_min-content]",
      "grid-rows-[min-content_auto_auto_auto_auto_auto_auto]",
      "sm:grid-rows-[min-content_min-content_min-content_1fr]",
      // XD-189: move animations take precedence over stale dimming.
      isSavingForLater
        ? "relative z-10 animate-lift-slide-out motion-reduce:animate-none"
        : isMovingToCart
          ? "relative z-10 animate-lift-slide-up-out motion-reduce:animate-none"
          : isStale && "opacity-50",
    )}
  >
    <div className="[grid-area:img]">
      <div className="sm:hidden">{imgSm}</div>
      <div className="hidden sm:block">{imgLg}</div>
    </div>

    <div className="[grid-area:name] sm:mb-3">{name}</div>

    <div className="[grid-area:varsActs]">
      {variants}
      {actions}
    </div>

    <div className="[grid-area:msg]">{messaging}</div>

    <div className="[grid-area:bundles]">{bundles}</div>

    <div className="flex min-w-min flex-col [grid-area:costAndRm] sm:self-center sm:justify-self-center sm:[grid-area:cost]">
      {cost}
    </div>

    <div className="flex min-w-min self-center [grid-area:qty] sm:justify-self-center">
      {qty}
    </div>

    <div className="flex min-w-min flex-col justify-end self-center justify-self-end [grid-area:total] sm:justify-center sm:justify-self-center">
      {total}
    </div>

    <div className="md:translate-0 h-4 min-w-min -translate-y-2 justify-self-end p-0 [grid-area:costAndRm] sm:self-center sm:[grid-area:rm]">
      {rm}
    </div>
  </li>
);

type MiniCartRowSlots = Pick<
  CartRowSlots,
  "imgSm" | "name" | "variants" | "cost" | "qty" | "rm" | "messaging"
>;

export const MiniCartRowLayout = ({
  isStale = false,
  isSavingForLater = false,
  isMovingToCart = false,
  imgSm,
  name,
  variants,
  cost,
  qty,
  rm,
  messaging,
}: MiniCartRowSlots & {
  isStale?: boolean;
  isSavingForLater?: boolean;
  isMovingToCart?: boolean;
}) => (
  <li
    className={cn(
      "grid gap-x-2 py-2",
      "[grid-template-areas:'img_name_name_name''img_vars_vars_vars''img_msg_msg_msg''img_cost_qty_rm']",
      "grid-cols-[auto_1fr_auto_auto]",
      // XD-189: move animations take precedence over stale dimming.
      isSavingForLater
        ? "relative z-10 animate-lift-slide-out motion-reduce:animate-none"
        : isMovingToCart
          ? "relative z-10 animate-lift-slide-up-out motion-reduce:animate-none"
          : isStale && "opacity-50",
    )}
  >
    <div className="[grid-area:img]">
      <div>{imgSm}</div>
    </div>

    <div className="max-w-full overflow-hidden [grid-area:name] sm:mb-3">
      {name}
    </div>

    <div className="[grid-area:vars]">{variants}</div>

    <div className="[grid-area:msg]">{messaging}</div>

    <div className="flex min-w-min flex-col self-center [grid-area:cost]">
      {cost}
    </div>

    <div className="flex min-w-min self-center justify-self-end [grid-area:qty]">
      {qty}
    </div>

    <div className="min-w-min self-center justify-self-end p-0 [grid-area:rm]">
      {rm}
    </div>
  </li>
);
